/**
 * Start-call / start-chat button — RN port of the web widget's
 * `components/action-button.tsx`.
 *
 * The label rules are what has to survive the crossing intact: the built-in
 * label depends on whether this is one of the paired start-screen buttons or
 * the single full-width one, and the integrator's `textBeforeCall` for this
 * button's own mode overrides it. An empty configured string falls back to the
 * built-in label rather than rendering a nameless button.
 */

import type { FC } from "react";
import { ActivityIndicator, StyleSheet, Text } from "react-native";
import type { RinggWidgetConfig } from "@ringg/core";

import { GradientFill } from "../lib/gradient";
import { ChatIcon, PhoneCallRingIcon } from "../lib/icons";
import { toViewStyle } from "../lib/portable-styles";
import { ringgId } from "../lib/testing";
import { resolveButtonRadius, resolveFontFamily, useWidgetTheme } from "../lib/widget-theme";
import { Touchable } from "./ui/pressable";

interface ActionButtonProps {
  isLoading?: boolean;
  isCalling?: boolean;
  callMode: "audio" | "text";
  buttonConfig?: RinggWidgetConfig["buttons"];
  // Renders as one of the paired start-screen buttons (Chat | Call)
  dual?: boolean;

  onCallTrigger: (callMode: ActionButtonProps["callMode"]) => void;
}

/** `h-4 w-4` on the leading icon. */
const ICON_SIZE = 16;

const CONNECTING_LABEL = "Connecting...";

const ActionButton: FC<ActionButtonProps> = ({ callMode, isLoading, buttonConfig, isCalling, dual = false, onCallTrigger }) => {
  const theme = useWidgetTheme();
  // `resolveButtonRadius` already maps "pill" onto the finite 9999 radius RN
  // needs, so web's separate pill branch has nothing left to decide here.
  const buttonRadius = resolveButtonRadius(theme.buttonStyle);

  // Dual: one of the side-by-side Chat/Call pair on the start screen — short
  // labels, each half the row. Single: the full-width fallback when the
  // integrator pins the widget to one mode (hideTabSelector).
  const defaultLabel = callMode === "audio" ? (dual ? "Call" : "Start Call") : dual ? "Chat" : "Start Chat";
  const label = (callMode === "audio" ? buttonConfig?.call?.textBeforeCall : buttonConfig?.text?.textBeforeCall) || defaultLabel;

  // Only the voice button reports progress; starting a chat is instant.
  const isConnecting = isLoading === true && callMode === "audio";

  // Web: `flex-1 h-10` when dual, otherwise `h-11` plus `w-full` before a call
  // and `w-fit px-6` during one. RN splits that in two — the touch target owns
  // the width, the painted surface owns the height and padding.
  const widthStyle = dual ? styles.dual : isCalling ? styles.compact : styles.full;
  const surfaceStyle = dual ? styles.dualSurface : isCalling ? styles.compactSurface : styles.singleSurface;
  const labelStyle = [styles.label, dual ? styles.dualLabel : styles.singleLabel, { color: theme.primaryTextColor, fontFamily: resolveFontFamily(theme.fontFamily) }];

  return (
    <Touchable testID={ringgId(callMode === "audio" ? "start-call-button" : "start-chat-button")} disabled={isLoading} onPress={() => onCallTrigger(callMode)} style={widthStyle}>
      <GradientFill color={theme.primaryColor} style={[styles.surface, surfaceStyle, { borderRadius: buttonRadius }, toViewStyle(buttonConfig?.call?.styles)]}>
        {isConnecting ? (
          <>
            {/* Web spins a LoaderCircle. The platform spinner says the same
                thing in the idiom users already know, and costs no animation
                loop of our own. */}
            <ActivityIndicator size="small" color={theme.primaryTextColor} />
            <Text style={labelStyle}>{CONNECTING_LABEL}</Text>
          </>
        ) : (
          <>
            {callMode === "audio" ? <PhoneCallRingIcon size={ICON_SIZE} fill={theme.primaryTextColor} /> : <ChatIcon size={ICON_SIZE} fill={theme.primaryTextColor} />}
            <Text style={labelStyle}>{label}</Text>
          </>
        )}
      </GradientFill>
    </Touchable>
  );
};

export default ActionButton;

const styles = StyleSheet.create({
  dual: { flex: 1 },
  full: { alignSelf: "stretch" },
  compact: { alignSelf: "flex-start" },

  // The layer <GradientFill> paints is absolutely positioned, so it sits out of
  // this row and the gap only ever spaces the icon from the label.
  surface: { flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 8 },
  dualSurface: { height: 40 },
  singleSurface: { height: 44 },
  compactSurface: { height: 44, paddingHorizontal: 24 },

  label: { fontWeight: "500" },
  dualLabel: { fontSize: 15 },
  // No text-size utility on the web button — it inherits the widget container's
  // 16px.
  singleLabel: { fontSize: 16 },
});
